<template>
  <div 
    v-if="hasFailedRecordings" 
    class="fixed top-4 right-4 z-50 bg-amber-50 border border-amber-200 rounded-lg p-4 shadow-lg max-w-sm"
    data-testid="persistent-audio-notification"
  >
    <div class="flex items-start gap-3">
      <div class="flex-shrink-0">
        <ExclamationTriangleIcon class="w-5 h-5 text-amber-600" />
      </div>
      <div class="flex-1">
        <h4 class="text-sm font-medium text-amber-800 mb-1">
          Failed Audio Messages
        </h4>
        <p class="text-sm text-amber-700 mb-3">
          You have {{ failedCount }} audio {{ failedCount === 1 ? 'message' : 'messages' }} that couldn't be sent. 
        </p>
        <div class="flex gap-2">
          <button 
            @click="showRecoveryOptions"
            class="text-xs px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors"
            data-testid="view-recover-button"
          >
            View & Recover
          </button>
          <button 
            @click="dismiss"
            class="text-xs px-3 py-1 border border-amber-300 text-amber-700 rounded hover:bg-amber-100 transition-colors"
            data-testid="dismiss-notification"
          >
            Dismiss
          </button>
        </div>
      </div>
      <button 
        @click="dismiss"
        class="flex-shrink-0 text-amber-500 hover:text-amber-700"
      >
        <XMarkIcon class="w-4 h-4" />
      </button>
    </div>
  </div>

  <!-- Recovery Modal -->
  <div 
    v-if="showRecovery" 
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    @click="closeRecovery"
  >
    <div 
      class="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-96 overflow-y-auto"
      @click.stop
    >
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-parchment-900">Failed Audio Messages</h3>
        <button @click="closeRecovery" class="text-parchment-500 hover:text-parchment-700" data-testid="close-recovery-modal">
          <XMarkIcon class="w-5 h-5" />
        </button>
      </div>
      
      <div class="space-y-3">
        <div 
          v-for="recording in failedRecordings" 
          :key="recording.recordingId"
          class="p-3 border border-parchment-200 rounded-lg"
          :data-testid="`recording-item-${recording.recordingId}`"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-parchment-900">
                {{ formatDate(recording.meta.timestamp) }}
              </p>
              <p class="text-xs text-parchment-600 mb-1">
                {{ recording.meta.size }} bytes • {{ recording.meta.mimeType }}
              </p>
              <p class="text-xs text-parchment-500 mb-2" data-testid="session-context">
                Single-thread session recording
              </p>
              <p class="text-xs text-red-600 mb-3" data-testid="error-message">{{ recording.meta.errorMessage }}</p>
              
              <div class="flex gap-2">
                <button 
                  v-if="recording.hasBlob"
                  @click="retryRecording(recording.recordingId)"
                  :disabled="isRetrying"
                  class="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-300"
                  data-testid="retry-upload"
                >
                  {{ isRetrying ? 'Retrying...' : 'Retry Upload' }}
                </button>
                <button 
                  v-if="recording.hasBlob"
                  @click="downloadRecording(recording.recordingId)"
                  class="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  data-testid="download-recording"
                >
                  Download
                </button>
                <button 
                  @click="contactSupport(recording.recordingId)"
                  class="text-xs px-2 py-1 bg-orange-600 text-white rounded hover:bg-orange-700"
                  data-testid="contact-support"
                >
                  Contact Support
                </button>
                <button 
                  @click="removeRecording(recording.recordingId)"
                  class="text-xs px-2 py-1 border border-parchment-300 text-parchment-700 rounded hover:bg-parchment-50"
                  data-testid="remove-recording"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="mt-4 pt-4 border-t border-parchment-200">
        <button 
          @click="clearAllRecordings"
          class="text-sm text-red-600 hover:text-red-800"
          data-testid="clear-all-recordings"
        >
          Clear All Failed Recordings
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { audioBufferManager } from '@/services/audioBuffer'
import { useExchangeStore } from '@/stores/exchange'

const exchangeStore = useExchangeStore()

// State
const dismissed = ref(false)
const showRecovery = ref(false)
const isRetrying = ref(false)
const failedRecordings = ref<Array<{ recordingId: string, meta: any, hasBlob: boolean }>>([])

// Computed
const hasFailedRecordings = computed(() => {
  return !dismissed.value && failedRecordings.value.length > 0
})

const failedCount = computed(() => failedRecordings.value.length)

// Methods
const loadFailedRecordings = () => {
  failedRecordings.value = audioBufferManager.getFailedRecordings()
}

const showRecoveryOptions = () => {
  showRecovery.value = true
}

const closeRecovery = () => {
  showRecovery.value = false
}

const dismiss = () => {
  dismissed.value = true
}

const downloadRecording = (recordingId: string) => {
  const success = audioBufferManager.downloadRecording(recordingId)
  if (success) {
    // Remove from our list after successful download
    failedRecordings.value = failedRecordings.value.filter(r => r.recordingId !== recordingId)
  }
}

const retryRecording = async (recordingId: string) => {
  isRetrying.value = true
  try {
    const success = await exchangeStore.retryFailedAudioMessage(recordingId)
    if (success) {
      // Remove from our list after successful retry
      failedRecordings.value = failedRecordings.value.filter(r => r.recordingId !== recordingId)
    }
  } catch (error) {
    console.error('Failed to retry recording:', error)
  } finally {
    isRetrying.value = false
  }
}

const contactSupport = (recordingId: string) => {
  audioBufferManager.contactSupport(recordingId, 'User contacted support from persistent notification')
}

const removeRecording = (recordingId: string) => {
  audioBufferManager.clearRecording(recordingId)
  failedRecordings.value = failedRecordings.value.filter(r => r.recordingId !== recordingId)
}

const clearAllRecordings = () => {
  failedRecordings.value.forEach(recording => {
    audioBufferManager.clearRecording(recording.recordingId)
  })
  failedRecordings.value = []
  showRecovery.value = false
  dismissed.value = true
}

const formatDate = (timestamp: string) => {
  const date = new Date(timestamp)
  return date.toLocaleString()
}

// Initialize
onMounted(() => {
  loadFailedRecordings()
  
  // Check for new failed recordings every 30 seconds
  const interval = setInterval(() => {
    loadFailedRecordings()
  }, 30000)
  
  // Cleanup interval on unmount
  return () => clearInterval(interval)
})
</script>